import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import FilterBar from '../components/FilterBar';
import ResortCard from '../components/ResortCard';
import { Loader2, SlidersHorizontal, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  
  // Comparison State
  const [selectedVillas, setSelectedVillas] = useState([]);

  const toggleCompare = (villa, isSelected) => {
    if (isSelected) {
      if (selectedVillas.length < 3) {
        // Enriched villa object with resort name if possible?
        // Actually ResortCard passes 'villa' and 'resort' props separately.
        // We need to construct a unified object or pass it.
        // Let's modify ResortCard later or just assume 'villa' has most info.
        // Wait, 'villa' doesn't have resort_name.
        // I should find the resort name from 'results'.
        const parentResort = results.find(r => r.villa.id === villa.id);
        const enrichedVilla = { ...villa, resort_name: parentResort?.resort_name };
        setSelectedVillas([...selectedVillas, enrichedVilla]);
      } else {
        alert("You can compare up to 3 villas.");
      }
    } else {
      setSelectedVillas(selectedVillas.filter(v => v.id !== villa.id));
    }
  };

  const fetchResults = async (searchQuery) => {
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/search`, {
        query: searchQuery
      });
      setResults(response.data.results);
      setAppliedFilters(response.data.applied_filters);
    } catch (error) {
      console.error("Search failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults(query);
  }, [query]);

  return (
    <Layout>
      <div className="bg-secondary/30 py-12 px-6 border-b border-border/40">
        <div className="max-w-4xl mx-auto">
          <FilterBar initialQuery={query} />
          
          {/* Active Filters Display */}
          {appliedFilters && (
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {Object.entries(appliedFilters).map(([key, value]) => {
                if (!value || (Array.isArray(value) && value.length === 0)) return null;
                return (
                  <div key={key} className="bg-white border border-border px-3 py-1 rounded-full text-xs text-primary/70 flex items-center gap-2 shadow-sm">
                    <span className="opacity-50 uppercase tracking-wider text-[10px]">{key.replace('_', ' ')}:</span>
                    <span className="font-medium">
                      {Array.isArray(value) ? value.join(', ') : value.toString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row gap-12">
        {/* Filters Sidebar (Static for now) */}
        <aside className="w-full md:w-64 flex-shrink-0 hidden md:block space-y-8">
           <div className="flex items-center gap-2 font-heading font-bold text-lg mb-6">
             <SlidersHorizontal size={20} />
             <span>Filters</span>
           </div>
           
           <div className="space-y-4">
             <h4 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Price Range</h4>
             <input type="range" className="w-full accent-primary" />
             <div className="flex justify-between text-xs text-muted-foreground">
               <span>$500</span>
               <span>$5000+</span>
             </div>
           </div>

           <div className="space-y-4">
             <h4 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Villa Type</h4>
             <div className="space-y-2">
               {['Overwater', 'Beach', 'Underwater'].map(type => (
                 <label key={type} className="flex items-center gap-3 text-sm hover:text-primary cursor-pointer">
                   <input type="checkbox" className="rounded-sm border-input text-primary focus:ring-primary" />
                   {type}
                 </label>
               ))}
             </div>
           </div>
        </aside>

        {/* Results Grid */}
        <div className="flex-1">
          <div className="mb-8 flex justify-between items-center">
            <h2 className="text-2xl font-heading font-medium">
              {loading ? "Searching..." : `${results.length} Villas Found`}
            </h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Sort by:</span>
              <select className="bg-transparent border-none font-medium text-primary focus:ring-0">
                <option>Relevance</option>
                <option>Price: Low to High</option>
                <option>Rating</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-primary" size={40} />
            </div>
          ) : (
            <div className="space-y-6">
              {results.map((item, index) => (
                <ResortCard 
                  key={item.villa.id} 
                  resort={item} 
                  villa={item.villa} 
                  delay={index}
                  onCompare={toggleCompare}
                  isSelected={selectedVillas.some(v => v.id === item.villa.id)}
                />
              ))}
              
              {results.length === 0 && (
                <div className="text-center py-20 bg-secondary/20 rounded-xl">
                  <h3 className="text-xl font-heading mb-2">No villas found</h3>
                  <p className="text-muted-foreground">Try adjusting your search terms.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Comparison Sticky Bar */}
      <AnimatePresence>
        {selectedVillas.length > 0 && (
          <motion.div 
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-border shadow-[0_-10px_40px_rgba(0,0,0,0.1)] p-4 z-50"
          >
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex gap-4">
                {selectedVillas.map(v => (
                  <div key={v.id} className="relative w-12 h-12 rounded-lg overflow-hidden border border-border">
                    <img src={v.images[0]} className="w-full h-full object-cover" />
                    <button 
                      onClick={() => toggleCompare(v, false)}
                      className="absolute top-0 right-0 bg-black/50 text-white p-0.5 rounded-bl"
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="flex flex-col justify-center ml-2">
                   <span className="font-bold text-primary">{selectedVillas.length} Selected</span>
                   <span className="text-xs text-muted-foreground">Compare features side-by-side</span>
                </div>
              </div>
              <button 
                onClick={() => navigate('/compare', { state: { villas: selectedVillas } })}
                className="bg-primary text-white px-8 py-3 rounded-full font-bold uppercase tracking-widest hover:bg-primary/90 transition-all flex items-center gap-2"
              >
                Compare Now <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default SearchPage;
